/**
 * AI Model Configuration Component
 *
 * Allows per-operation model selection between Claude (Haiku, Sonnet, Opus)
 * and Ollama local models for cost optimization.
 *
 * Data is stored in database and synced across the account.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { aiConfigApi, type AIOperationConfig } from '@/lib/api/client'
import { useAuthStore } from '@/stores/authStore'
import {
  Cpu,
  Cloud,
  Server,
  Zap,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Loader2,
  DollarSign,
  Sparkles,
  Save,
} from 'lucide-react'

// ============================================================================
// MODEL METADATA
// ============================================================================

interface ModelInfo {
  id: string
  name: string
  provider: 'claude' | 'gemini' | 'ollama'
  speed: 'fast' | 'medium' | 'slow'
  quality: 'good' | 'great' | 'best'
  costTier: string
}

const CLAUDE_MODELS: ModelInfo[] = [
  {
    id: 'claude-haiku-4-5-20251001',
    name: 'Haiku',
    provider: 'claude',
    speed: 'fast',
    quality: 'good',
    costTier: '$0.80/$4',
  },
  {
    id: 'claude-sonnet-4-20250514',
    name: 'Sonnet',
    provider: 'claude',
    speed: 'medium',
    quality: 'great',
    costTier: '$3/$15',
  },
  {
    id: 'claude-opus-4-20250514',
    name: 'Opus',
    provider: 'claude',
    speed: 'slow',
    quality: 'best',
    costTier: '$15/$75',
  },
]

const GEMINI_MODELS: ModelInfo[] = [
  {
    id: 'gemini-2.0-flash-exp',
    name: 'Flash 2.0',
    provider: 'gemini',
    speed: 'fast',
    quality: 'great',
    costTier: 'FREE',
  },
  {
    id: 'gemini-1.5-flash',
    name: 'Flash 1.5',
    provider: 'gemini',
    speed: 'fast',
    quality: 'good',
    costTier: '$0.075/$0.30',
  },
  {
    id: 'gemini-1.5-flash-8b',
    name: 'Flash 8B',
    provider: 'gemini',
    speed: 'fast',
    quality: 'good',
    costTier: '$0.0375/$0.15',
  },
  {
    id: 'gemini-1.5-pro',
    name: 'Pro 1.5',
    provider: 'gemini',
    speed: 'medium',
    quality: 'best',
    costTier: '$1.25/$5',
  },
  {
    id: 'gemini-2.0-flash-thinking-exp',
    name: 'Thinking',
    provider: 'gemini',
    speed: 'slow',
    quality: 'best',
    costTier: 'FREE',
  },
]

const OLLAMA_MODELS: ModelInfo[] = [
  {
    id: 'phi3:medium',
    name: 'Phi-3 14B',
    provider: 'ollama',
    speed: 'medium',
    quality: 'best',
    costTier: 'free',
  },
  {
    id: 'gemma2:9b',
    name: 'Gemma 2 9B',
    provider: 'ollama',
    speed: 'medium',
    quality: 'great',
    costTier: 'free',
  },
  {
    id: 'mistral:7b',
    name: 'Mistral 7B',
    provider: 'ollama',
    speed: 'fast',
    quality: 'great',
    costTier: 'free',
  },
  {
    id: 'llama3.2:3b',
    name: 'Llama 3.2 3B',
    provider: 'ollama',
    speed: 'fast',
    quality: 'good',
    costTier: 'free',
  },
  {
    id: 'qwen2.5:7b',
    name: 'Qwen 2.5 7B',
    provider: 'ollama',
    speed: 'medium',
    quality: 'great',
    costTier: 'free',
  },
]

const ALL_MODELS = [...CLAUDE_MODELS, ...GEMINI_MODELS, ...OLLAMA_MODELS]

// ============================================================================
// CATEGORY GROUPING
// ============================================================================

interface CategoryGroup {
  id: string
  label: string
}

const OPERATION_CATEGORIES: Record<string, CategoryGroup> = {
  discovery: { id: 'discovery', label: 'Discovery' },
  canvas: { id: 'canvas', label: 'Canvas' },
  document: { id: 'document', label: 'Document Analysis' },
  utility: { id: 'utility', label: 'Utility' },
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AIModelConfig() {
  const { currentAccount } = useAuthStore()
  const queryClient = useQueryClient()
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['discovery'])
  )
  const [ollamaAvailable, setOllamaAvailable] = useState(false)
  const [ollamaModels, setOllamaModels] = useState<string[]>([])
  const [isCheckingOllama, setIsCheckingOllama] = useState(false)

  // Fetch operation configs
  const { data, isLoading } = useQuery({
    queryKey: ['aiOperations', currentAccount?.id],
    queryFn: () => aiConfigApi.getOperations(currentAccount!.id),
    enabled: !!currentAccount?.id,
  })

  const operations = data?.operations || []

  // Update operation mutation
  const updateMutation = useMutation({
    mutationFn: ({
      operation,
      config,
    }: {
      operation: string
      config: { provider?: 'claude' | 'gemini' | 'ollama'; model?: string }
    }) => {
      console.log('[AIModelConfig] 🔵 Mutation executing:', { operation, config, accountId: currentAccount?.id })
      if (!currentAccount?.id) {
        console.error('[AIModelConfig] ❌ No currentAccount!')
        throw new Error('No account selected')
      }
      return aiConfigApi.updateOperation(currentAccount.id, operation, config)
    },
    onSuccess: () => {
      console.log('[AIModelConfig] ✅ Mutation succeeded')
      queryClient.invalidateQueries({ queryKey: ['aiOperations', currentAccount?.id] })
    },
    onError: (error) => {
      console.error('[AIModelConfig] ❌ Mutation failed:', error)
    },
  })

  // Delete override mutation (revert to default)
  const deleteMutation = useMutation({
    mutationFn: (operation: string) => aiConfigApi.deleteOverride(currentAccount!.id, operation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiOperations', currentAccount?.id] })
    },
  })

  // Check Ollama availability
  const checkOllamaStatus = async () => {
    setIsCheckingOllama(true)
    try {
      const response = await fetch('http://localhost:11434/api/tags', {
        method: 'GET',
        signal: AbortSignal.timeout(3000),
      })
      if (response.ok) {
        const dataRes = await response.json()
        const models = dataRes.models?.map((m: { name: string }) => m.name) || []
        setOllamaAvailable(true)
        setOllamaModels(models)
      } else {
        setOllamaAvailable(false)
        setOllamaModels([])
      }
    } catch {
      setOllamaAvailable(false)
      setOllamaModels([])
    } finally {
      setIsCheckingOllama(false)
    }
  }

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  // Group operations by category
  const operationsByCategory = operations.reduce(
    (acc, op) => {
      if (!acc[op.category]) {
        acc[op.category] = []
      }
      acc[op.category].push(op)
      return acc
    },
    {} as Record<string, AIOperationConfig[]>
  )

  // Calculate summary stats
  const summary = operations.reduce(
    (acc, op) => {
      if (op.provider === 'ollama') {
        acc.free++
      } else {
        acc.paid++
      }
      return acc
    },
    { free: 0, paid: 0 }
  )

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-lg bg-market/10 flex items-center justify-center flex-shrink-0">
              <Cpu className="w-5 h-5 text-market" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold whitespace-nowrap">
                Per-Operation Model Config
              </CardTitle>
              <CardDescription className="text-text-muted whitespace-nowrap">
                Tune each AI operation for cost vs quality
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-market/10 text-market border border-market/20">
              {summary.free} free
            </Badge>
            <Badge className="bg-business/10 text-business border border-business/20">
              {summary.paid} paid
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Ollama Status */}
        <div className="flex items-center justify-between p-3 bg-warm-50 rounded-lg border border-border-subtle">
          <div className="flex items-center gap-3">
            <Server className="w-5 h-5 text-text-muted" />
            <div>
              <p className="text-sm font-medium text-text">Ollama (Local)</p>
              <p className="text-xs text-text-muted">
                {ollamaAvailable
                  ? `${ollamaModels.length} models detected by browser`
                  : 'Not detected by browser (API may still access it)'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isCheckingOllama ? (
              <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
            ) : ollamaAvailable ? (
              <CheckCircle className="w-4 h-4 text-business" />
            ) : (
              <AlertCircle className="w-4 h-4 text-warm-500" />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={checkOllamaStatus}
              disabled={isCheckingOllama}
              title="Check if browser can access Ollama"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Operation Categories */}
        <div className="space-y-2">
          {Object.entries(operationsByCategory).map(([category, ops]) => {
            const categoryInfo = OPERATION_CATEGORIES[category]
            if (!categoryInfo) return null

            const isExpanded = expandedCategories.has(category)
            const freeCount = ops.filter((o) => o.provider === 'ollama').length

            return (
              <div key={category} className="border border-border-subtle rounded-lg overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between p-3 bg-warm-50 hover:bg-warm-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-text-muted" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-text-muted" />
                    )}
                    <span className="text-sm font-medium text-text">{categoryInfo.label}</span>
                    <Badge variant="outline" className="text-xs">
                      {ops.length} ops
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-market/10 text-market border-none text-xs">
                      {freeCount} free
                    </Badge>
                    <Badge className="bg-business/10 text-business border-none text-xs">
                      {ops.length - freeCount} paid
                    </Badge>
                  </div>
                </button>

                {/* Operations List */}
                {isExpanded && (
                  <div className="divide-y divide-border-subtle">
                    {ops.map((op) => (
                      <OperationRow
                        key={op.operation}
                        operation={op}
                        ollamaAvailable={ollamaAvailable}
                        ollamaModels={ollamaModels}
                        onModelChange={(model) => {
                          console.log('[AIModelConfig] 🟡 onModelChange called:', { model })
                          const provider = ALL_MODELS.find((m) => m.id === model)?.provider
                          console.log('[AIModelConfig] 🟡 Found provider:', { provider, model })
                          console.log('[AIModelConfig] 🟡 Calling mutation.mutate...')
                          updateMutation.mutate({
                            operation: op.operation,
                            config: { provider, model },
                          })
                        }}
                        onReset={() => deleteMutation.mutate(op.operation)}
                        isUpdating={updateMutation.isPending || deleteMutation.isPending}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Cost Info */}
        <div className="flex items-start gap-3 p-3 bg-market/5 border border-market/20 rounded-lg">
          <DollarSign className="w-4 h-4 text-market flex-shrink-0 mt-0.5" />
          <div className="text-xs text-text-secondary">
            <strong>Cost savings:</strong> Operations using Ollama are completely free. Claude Haiku
            is cheapest ($0.80/$4 per MTok), Sonnet is mid-tier ($3/$15), and Opus is premium
            ($15/$75).
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// OPERATION ROW
// ============================================================================

interface OperationRowProps {
  operation: AIOperationConfig
  ollamaAvailable: boolean
  ollamaModels: string[]
  onModelChange: (model: string) => void
  onReset: () => void
  isUpdating: boolean
}

function OperationRow({
  operation,
  ollamaAvailable,
  ollamaModels,
  onModelChange,
  onReset,
  isUpdating,
}: OperationRowProps) {
  const [isOpen, setIsOpen] = useState(false)

  const currentModel = ALL_MODELS.find((m) => m.id === operation.model)
  const isFree = operation.provider === 'ollama'
  const showOllamaOptions = !operation.requiresVision
  const isGemini = operation.provider === 'gemini'

  // Filter Ollama models based on what's actually available
  const availableOllamaModels = ollamaAvailable
    ? OLLAMA_MODELS.filter((m) => ollamaModels.some((om) => om.startsWith(m.id.split(':')[0])))
    : []

  return (
    <div className="p-3">
      <div className="flex items-center justify-between gap-4">
        {/* Operation Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text">{operation.name}</span>
            {operation.requiresVision && (
              <Badge variant="outline" className="text-xs">
                Vision
              </Badge>
            )}
            {operation.isOverridden && (
              <Badge variant="outline" className="text-xs bg-business/5 text-business border-business/20">
                Custom
              </Badge>
            )}
          </div>
          <p className="text-xs text-text-muted truncate">{operation.description}</p>
        </div>

        {/* Current Selection */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsOpen(!isOpen)}
            disabled={isUpdating}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
              isFree
                ? 'bg-market/5 border-market/20 text-market'
                : isGemini
                  ? 'bg-solution/5 border-solution/20 text-solution'
                  : 'bg-business/5 border-business/20 text-business'
            } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isFree ? (
              <Server className="w-3.5 h-3.5" />
            ) : isGemini ? (
              <Sparkles className="w-3.5 h-3.5" />
            ) : (
              <Cloud className="w-3.5 h-3.5" />
            )}
            <span className="text-xs font-medium">{currentModel?.name || operation.model}</span>
            {isUpdating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            )}
          </button>

          {operation.isOverridden && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onReset}
              disabled={isUpdating}
              className="h-7 w-7"
            >
              <RotateCcw className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Model Selector Dropdown */}
      {isOpen && (
        <div className="mt-3 p-3 bg-warm-50 rounded-lg space-y-3">
          {/* Claude Models */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Cloud className="w-3.5 h-3.5 text-business" />
              <span className="text-xs font-medium text-text-secondary">Claude (Paid)</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {CLAUDE_MODELS.map((model) => {
                const isActive = operation.model === model.id
                return (
                  <button
                    key={model.id}
                    onClick={() => {
                      onModelChange(model.id)
                      setIsOpen(false)
                    }}
                    disabled={isUpdating}
                    className={`flex flex-col items-center gap-0.5 p-2 rounded border transition-all ${
                      isActive
                        ? 'border-business bg-business/10'
                        : 'border-border-subtle hover:border-business/50 bg-white'
                    }`}
                  >
                    <span
                      className={`text-xs font-medium ${isActive ? 'text-business' : 'text-text-secondary'}`}
                    >
                      {model.name}
                    </span>
                    <div className="flex items-center gap-1">
                      {model.speed === 'fast' && <Zap className="w-2.5 h-2.5 text-business" />}
                      <span className="text-[10px] text-text-muted">{model.costTier}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Gemini Models */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-solution" />
              <span className="text-xs font-medium text-text-secondary">Gemini (Paid)</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {GEMINI_MODELS.map((model) => {
                const isActive = operation.model === model.id
                const isFreeModel = model.costTier === 'FREE'
                return (
                  <button
                    key={model.id}
                    onClick={() => {
                      console.log('[AIModelConfig] 🟢 Gemini button clicked:', { modelId: model.id, modelName: model.name, isUpdating })
                      onModelChange(model.id)
                      setIsOpen(false)
                    }}
                    disabled={isUpdating}
                    className={`flex flex-col items-center gap-0.5 p-2 rounded border transition-all ${
                      isActive
                        ? 'border-solution bg-solution/10'
                        : 'border-border-subtle hover:border-solution/50 bg-white'
                    }`}
                  >
                    <span
                      className={`text-xs font-medium ${isActive ? 'text-solution' : 'text-text-secondary'}`}
                    >
                      {model.name}
                    </span>
                    <div className="flex items-center gap-1">
                      {model.speed === 'fast' && <Zap className="w-2.5 h-2.5 text-solution" />}
                      <span className={`text-[10px] ${isFreeModel ? 'text-solution font-semibold' : 'text-text-muted'}`}>
                        {model.costTier}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Ollama Models */}
          {showOllamaOptions && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-3.5 h-3.5 text-market" />
                <span className="text-xs font-medium text-text-secondary">Ollama (Free, Local)</span>
                {!ollamaAvailable && (
                  <Badge variant="outline" className="text-xs text-solution">
                    Not running
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(ollamaAvailable ? availableOllamaModels : OLLAMA_MODELS.slice(0, 3)).map(
                  (model) => {
                    const isActive = operation.model === model.id
                    const isAvailable =
                      ollamaAvailable &&
                      ollamaModels.some((m) => m.startsWith(model.id.split(':')[0]))
                    return (
                      <button
                        key={model.id}
                        onClick={() => {
                          onModelChange(model.id)
                          setIsOpen(false)
                        }}
                        disabled={isUpdating}
                        className={`flex flex-col items-center gap-0.5 p-2 rounded border transition-all ${
                          isActive
                            ? 'border-market bg-market/10'
                            : 'border-border-subtle hover:border-market/50 bg-white'
                        } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`text-xs font-medium ${isActive ? 'text-market' : 'text-text-secondary'}`}
                        >
                          {model.name}
                        </span>
                        <div className="flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5 text-market" />
                          <span className="text-[10px] text-text-muted">free</span>
                          {ollamaAvailable && !isAvailable && (
                            <span className="text-[10px] text-solution">(pull)</span>
                          )}
                        </div>
                      </button>
                    )
                  }
                )}
              </div>
              {!ollamaAvailable && (
                <div className="mt-2 p-2 bg-market/5 border border-market/20 rounded">
                  <p className="text-[10px] text-text-muted">
                    <strong>Note:</strong> Browser can't detect Ollama, but the API server can still use it.
                    Models will work if Ollama is running on your system.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AIModelConfig
